import { useState, useEffect } from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { auth, getUserCollection, toggleFavorite } from "@/lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useToast } from "@/hooks/use-toast";
import { SearchBar } from "@/components/cards/SearchBar";

const CollectionPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [collection, setCollection] = useState<any[]>([]);
  const [filteredCollection, setFilteredCollection] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  
  // Check authentication status on component mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setIsLoggedIn(!!currentUser);
      
      if (currentUser) {
        try {
          setIsLoading(true);
          const cards = await getUserCollection();
          setCollection(cards);
          setFilteredCollection(cards);
        } catch (error) {
          console.error("Error fetching collection:", error);
          toast({
            title: "Error",
            description: "Failed to load your collection. Please try again later.",
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        setCollection([]);
        setFilteredCollection([]);
        setIsLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, [toast]);

  // Filter collection when search query changes
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredCollection(collection);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = collection.filter(card => 
        card.cardData?.name?.toLowerCase().includes(query) ||
        card.cardData?.set?.name?.toLowerCase().includes(query)
      );
      setFilteredCollection(filtered);
    }
  }, [searchQuery, collection]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleToggleFavorite = async (cardId: string, isFavorite: boolean) => {
    try {
      await toggleFavorite(cardId);
      
      // Update local state to reflect change
      setCollection(prevCollection => 
        prevCollection.map(card => 
          card.id === cardId ? { ...card, isFavorite: !isFavorite } : card
        )
      );

      toast({
        title: isFavorite ? "Removed from favorites" : "Added to favorites",
        description: `Card has been ${isFavorite ? "removed from" : "added to"} your favorites.`,
      });
    } catch (error) {
      console.error("Error toggling favorite status:", error);
      toast({
        title: "Error",
        description: "Failed to update favorite status.",
        variant: "destructive",
      });
    }
  };
  
  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-gray-200 rounded mx-auto"></div>
            <div className="h-24 max-w-md bg-gray-100 rounded"></div>
            <div className="h-64 bg-gray-100 rounded"></div>
          </div>
        </div>
      </PageContainer>
    );
  }
  
  if (!isLoggedIn) {
    return (
      <PageContainer>
        <div className="max-w-md mx-auto py-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-center">Sign in to view your collection</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-muted-foreground mb-6">
                You need to login or create an account to manage your card collection.
              </p>
              <div className="space-y-2">
                <Button 
                  className="w-full bg-pokemon-blue hover:bg-pokemon-dark-blue"
                  onClick={() => navigate("/profile")}
                >
                  Sign In
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate("/profile")}
                >
                  Create Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    );
  }
  
  const collectionValue = collection.reduce((sum, card) => {
    return sum + (card.cardData?.price || 0);
  }, 0);
  
  const rareCards = collection.filter(card => 
    (card.cardData?.price || 0) >= 20
  ).length;

  // Get favorite cards
  const favoriteCards = filteredCollection.filter(card => card.isFavorite);
  
  return (
    <PageContainer>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Your Collection</h1>
        <Button onClick={() => navigate("/scan")}>
          <Plus className="mr-2 h-4 w-4" />
          Add Card
        </Button>
      </div>
      
      <Card className="mb-8">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-4">
              <h3 className="text-3xl font-bold text-pokemon-blue mb-1">{collection.length}</h3>
              <p className="text-sm text-muted-foreground">Total Cards</p>
            </div>
            <div className="p-4">
              <h3 className="text-3xl font-bold text-pokemon-blue mb-1">${collectionValue.toFixed(2)}</h3>
              <p className="text-sm text-muted-foreground">Collection Value</p>
            </div>
            <div className="p-4">
              <h3 className="text-3xl font-bold text-pokemon-blue mb-1">{rareCards}</h3>
              <p className="text-sm text-muted-foreground">Rare Cards</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Search bar - added above the tabs */}
      <div className="mb-4">
        <SearchBar 
          onSearch={handleSearch}
          placeholder="Search your collection..."
        />
      </div>
      
      <Tabs defaultValue="all" className="mb-8">
        <TabsList className="mb-4">
          <TabsTrigger value="all">All Cards</TabsTrigger>
          <TabsTrigger value="recent">Recently Added</TabsTrigger>
          <TabsTrigger value="valuable">Most Valuable</TabsTrigger>
          <TabsTrigger value="favorites">Favorites</TabsTrigger>
          <TabsTrigger value="sets">Sets</TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          {filteredCollection.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-xl font-semibold mb-2">
                {searchQuery ? "No matching cards found" : "Your collection is empty"}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery 
                  ? "Try a different search term or browse your collection."
                  : "Start scanning cards or add them manually to build your collection."}
              </p>
              {!searchQuery && (
                <Button onClick={() => navigate("/scan")} className="bg-pokemon-blue hover:bg-pokemon-dark-blue">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Your First Card
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredCollection.map((card) => (
                <div 
                  key={card.id} 
                  className="border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => card.cardData?.id && navigate(`/card/${card.cardData.id}`)}
                >
                  {card.cardData?.imageUrl && (
                    <div className="relative">
                      <img
                        src={card.cardData.imageUrl}
                        alt={card.cardData.name}
                        className="w-full h-48 object-contain mb-2"
                      />
                      {card.isFavorite && (
                        <div className="absolute top-1 right-1 bg-white rounded-full p-1 shadow">
                          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  )}
                  <p className="font-medium truncate">{card.cardData.name}</p>
                  <p className="text-sm text-muted-foreground">${card.cardData.price?.toFixed(2) || '0.00'}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="recent">
          {filteredCollection.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-xl font-semibold mb-2">No recently added cards</h3>
              <p className="text-muted-foreground mb-6">
                Cards you add will appear here.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredCollection
                .sort((a, b) => b.addedAt?.toDate?.() - a.addedAt?.toDate?.() || 0)
                .slice(0, 8)
                .map((card) => (
                  <div 
                    key={card.id} 
                    className="border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => card.cardData?.id && navigate(`/card/${card.cardData.id}`)}
                  >
                    {card.cardData.imageUrl && (
                      <div className="relative">
                        <img
                          src={card.cardData.imageUrl}
                          alt={card.cardData.name}
                          className="w-full h-48 object-contain mb-2"
                        />
                        {card.isFavorite && (
                          <div className="absolute top-1 right-1 bg-white rounded-full p-1 shadow">
                            <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="font-medium truncate">{card.cardData.name}</p>
                    <p className="text-sm text-muted-foreground">${card.cardData.price?.toFixed(2) || '0.00'}</p>
                  </div>
                ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="valuable">
          {filteredCollection.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-xl font-semibold mb-2">No valuable cards</h3>
              <p className="text-muted-foreground mb-6">
                Add cards to your collection to see their values.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredCollection
                .sort((a, b) => (b.cardData?.price || 0) - (a.cardData?.price || 0))
                .slice(0, 8)
                .map((card) => (
                  <div 
                    key={card.id} 
                    className="border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => card.cardData?.id && navigate(`/card/${card.cardData.id}`)}
                  >
                    {card.cardData.imageUrl && (
                      <div className="relative">
                        <img
                          src={card.cardData.imageUrl}
                          alt={card.cardData.name}
                          className="w-full h-48 object-contain mb-2"
                        />
                        {card.isFavorite && (
                          <div className="absolute top-1 right-1 bg-white rounded-full p-1 shadow">
                            <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="font-medium truncate">{card.cardData.name}</p>
                    <p className="text-sm text-muted-foreground">${card.cardData.price?.toFixed(2) || '0.00'}</p>
                  </div>
                ))}
            </div>
          )}
        </TabsContent>
        
        {/* New Favorites tab */}
        <TabsContent value="favorites">
          {favoriteCards.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-xl font-semibold mb-2">No favorite cards</h3>
              <p className="text-muted-foreground mb-6">
                Add cards to your favorites by clicking the heart icon on any card.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {favoriteCards.map((card) => (
                <div 
                  key={card.id} 
                  className="border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => card.cardData?.id && navigate(`/card/${card.cardData.id}`)}
                >
                  {card.cardData.imageUrl && (
                    <div className="relative">
                      <img
                        src={card.cardData.imageUrl}
                        alt={card.cardData.name}
                        className="w-full h-48 object-contain mb-2"
                      />
                      <div className="absolute top-1 right-1 bg-white rounded-full p-1 shadow">
                        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                  <p className="font-medium truncate">{card.cardData.name}</p>
                  <p className="text-sm text-muted-foreground">${card.cardData.price?.toFixed(2) || '0.00'}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="sets">
          <div className="text-center py-16">
            <h3 className="text-xl font-semibold mb-2">No sets collected</h3>
            <p className="text-muted-foreground mb-6">
              Add cards to start tracking your set completion progress.
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
};

export default CollectionPage;
